import {
  Box,
  Checkbox,
  CollapsibleCard,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { InformationIcon, OpenInNewIcon } from '@atgfr8/icons';
import { SelectInput } from '@components';
import { AutoComplete } from '@components/Autocomplete';
import { Notification } from '@components/Notification';
import { FeatureFlags } from '@constants/featureFlags';
import { customerUrl } from '@constants/urls';
import { A } from '@solidjs/router';
import {
  BusinessTypeSelect,
  customerDetailsStore,
  updateCustomerDetailsState,
} from '@store/customers/customerDetails';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { customerTiers } from '@store/orders/constant';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { formatAmount, isAdmin, isReadonly } from '@utils/utils';
import get from 'lodash/get';
import { DateTime } from 'luxon';
import { Show } from 'solid-js';

import {
  blockingCustomerStatuses,
  customerStatuses,
  getCustomerSubstatuses,
  statusHasSubstatuses,
  trackingVisibilityProviderList,
} from '../constants';
import classes from './classes';

const getCustomerStatusColor = {
  Pending: '#E4E6EF',
  'In Review': '#E4E6EF',
  Customer: '#67E667',
  'Legal Review': '#f9d36b',
  'Credit Hold': '#F97C6B',
  'Collection Hold': '#F97C6B',
  Inactive: '#E4E6EF',
  'In Collections': '#E4E6EF',
  Deactivated: '#F97C6B',
  Duplicate: '#E4E6EF',
  Dormant: '#F97C6B',
  Bankruptcy: '#F97C6B',
  'Credit Review': '#f9d36b',
  Prepay: '#f9d36b',
  RFP: '#f9d36b',
  Archived: '#E4E6EF',
};

const CustomerInformation = () => {
  const loadInstructionsPlaceholder =
    'This is an area for Special Instructions that you want to appear on any Loads you build for this customer.  This will appear on all Carrier Rate Confirmations.';
  const bolInstructionsPlaceholder =
    'This is an area for Special Instructions that you want to appear on any Loads you build for this customer. This will appear on all Bills of Lading.';
  const pointOfSaleInstructionsPlaceholder =
    'This is an area for Special Instructions that you want to appear on any Loads you build for this customer. This is intended for INTERNAL use only and will appear within the Customer POS section of load. This will NOT appear on the rate confirmation or BOL.';
  const canSeeNegativeCredit = isFeatureFlagEnabled(
    FeatureFlags.ViewNegativeCredit,
  );

  const onBusinessTypeSelect = (item: BusinessTypeSelect) => {
    updateCustomerDetailsState({
      businessTypeDisplayName: item.name,
      businessTypeId: item.id,
    });
  };

  const handlePcMilerVersionChange = (value: string) => {
    const sanitizedValue = value.replace(/[^0-9.]/g, '');
    updateCustomerDetailsState({
      pcMilerVersion: sanitizedValue,
    });
  };

  const handleCreditUtilization = () => {
    const creditLimit = customerDetailsStore.customer.creditLimit ?? 0;
    if (creditLimit === 0) return '0.00%';
    const availableCredit = get(
      customerDetailsStore.customer,
      'financials.availableCredit',
      0,
    );
    const compare = creditLimit - availableCredit;
    const creditUtilization = (compare / creditLimit) * 100;
    return `${
      !isNaN(creditUtilization) ? creditUtilization.toFixed(2) : '0.0'
    }%`;
  };

  const getAvailableCredit = (): number | null =>
    get(customerDetailsStore.customer, 'financials.availableCredit') ?? null;

  const getNegativeCreditLimit = () => {
    const allowance =
      customerDetailsStore.customer.negativeCreditAllowance ?? 0;
    return allowance > 0 ? -allowance : allowance;
  };

  const handleAbsoluteCredit = (): number | null => {
    const availableCredit = getAvailableCredit();
    if (availableCredit === null) return null;
    return Math.abs(availableCredit - getNegativeCreditLimit());
  };

  const absoluteCreditTooltip = () => (
    <div class="absolute-credit-tooltip flex flex-col gap-1 whitespace-nowrap">
      <span>Absolute Credit = Available Credit - (negative credit limit)</span>
      <span>
        {formatAmount(handleAbsoluteCredit())} ={' '}
        {formatAmount(getAvailableCredit())} - (
        {formatAmount(getNegativeCreditLimit())})
      </span>
    </div>
  );

  const handleTrimmedInput = (field: string) => (e: Event) => {
    const target = e.target as HTMLInputElement | null;
    const value = target?.value ?? '';
    const cleaned = value.replace(/\s+/g, ' ').trim();
    updateCustomerDetailsState({ [field]: cleaned });
  };

  return (
    <CollapsibleCard startTitle="Customer Information" class="mb-2">
      <>
        <Show when={get(customerDetailsStore.customer, 'ediRequest')}>
          <Box class="mb-4">
            <Notification
              type="error"
              text={
                <>
                  <Typography variant="body1" class="font-medium!">
                    EDI Setup
                  </Typography>
                  This customer has a pending EDI setup request. It's current
                  status is{' '}
                  <strong class="font-medium!">
                    {get(customerDetailsStore.customer, 'ediRequest.status')}
                  </strong>
                </>
              }
            />
          </Box>
        </Show>
        <Show when={customerDetailsStore.customer.status === 'In Review'}>
          <Box class="mb-4">
            <Notification
              type="warning"
              text={
                <>
                  This Customer is currently under Credit Review. Response could
                  take <strong>UP TO</strong> 24 hours.
                </>
              }
            />
          </Box>
        </Show>
        <Stack direction="row" gap={2} mb={3}>
          <div class="flex-1">
            <TextField
              label="Customer Name"
              value={customerDetailsStore.customer.name ?? ''}
              onChange={(value: string) => {
                updateCustomerDetailsState({ name: value });
              }}
              on:blur={handleTrimmedInput('name')}
              maxLength={50}
              disabled={isReadonly()}
              dataTestId="customer-information-customer-name-textInput"
            />
          </div>
          <div class="flex-1">
            <TextField
              label="Legal Name"
              value={customerDetailsStore.customer.legalName ?? ''}
              onChange={(value: string) => {
                updateCustomerDetailsState({ legalName: value });
              }}
              on:blur={handleTrimmedInput('legalName')}
              maxLength={50}
              disabled={isReadonly()}
              dataTestId="customer-information-legal-name-textInput"
            />
          </div>
          <div class="flex-1">
            <SelectInput
              style={{
                'background-color':
                  getCustomerStatusColor[
                    get(
                      customerDetailsStore.customer,
                      'status',
                    ) as keyof typeof getCustomerStatusColor
                  ],
              }}
              label="Customer Status"
              onChange={(newStatus) => {
                if (
                  !isFeatureFlagEnabled(
                    FeatureFlags.ShowBlockingCustomerStatuses,
                  )() &&
                  blockingCustomerStatuses.includes(newStatus)
                ) {
                  return;
                }
                updateCustomerDetailsState({
                  status: newStatus,
                  subStatus: null,
                });
              }}
              menuItems={customerStatuses().map((s) => ({
                label: s.label,
                value: String(s.value),
              }))}
              value={customerDetailsStore.customer.status ?? ''}
              disabled={isReadonly()}
              dataTestId={formatDataTestId('Customer status', 'selectInput')}
            />
          </div>
        </Stack>
        <div class="flex flex-wrap gap-2">
          <Show
            when={statusHasSubstatuses(customerDetailsStore.customer.status)}
          >
            <div class="min-w-35 flex-1">
              <SelectInput
                label="Customer Substatus"
                onChange={(val) =>
                  updateCustomerDetailsState({
                    subStatus: val || null,
                  })
                }
                menuItems={getCustomerSubstatuses(
                  customerDetailsStore.customer.status,
                ).map((s) => ({
                  label: s.label,
                  value: String(s.value),
                }))}
                value={customerDetailsStore.customer.subStatus ?? ''}
                disabled={
                  isReadonly() ||
                  !isFeatureFlagEnabled(
                    FeatureFlags.ShowBlockingCustomerStatuses,
                  )()
                }
                dataTestId={formatDataTestId(
                  'Customer substatus',
                  'selectInput',
                )}
              />
            </div>
          </Show>
          <div class="min-w-30 flex-1">
            <SelectInput
              label="Customer Tier"
              onChange={(val) =>
                updateCustomerDetailsState({
                  customerTier: val && val !== 'null' ? val : null,
                })
              }
              menuItems={customerTiers.map((t) => ({
                label: t.label,
                value: String(t.value),
              }))}
              value={
                customerDetailsStore.customer.customerTier === 'null'
                  ? ''
                  : (customerDetailsStore.customer.customerTier ?? '')
              }
              disabled={isReadonly()}
              dataTestId={formatDataTestId('Customer tier', 'selectInput')}
            />
          </div>
          <div class="min-w-40">
            <AutoComplete
              endpoint="businesstype/autocomplete/?term="
              version="v3"
              responseFieldName="name"
              onItemSelect={(item) => {
                onBusinessTypeSelect(item as BusinessTypeSelect);
              }}
              id="autocomplete-field-bussiness"
              label="Add a Business Type"
              defaultValue=""
              value={
                customerDetailsStore.customer.businessTypeDisplayName ?? ''
              }
              disabled={isReadonly()}
              onClearValue={() => {
                updateCustomerDetailsState({
                  businessTypeDisplayName: '',
                  businessTypeId: null,
                });
              }}
            />
          </div>
          <Show when={isAdmin()}>
            <div class="min-w-0 w-full max-w-[130px] sm:w-[120px] shrink-0">
              <TextField
                label="Intacct ID"
                value={customerDetailsStore.customer.accountingSysId ?? ''}
                onChange={(value: string) =>
                  updateCustomerDetailsState({ accountingSysId: value })
                }
                maxLength={50}
                disabled={isReadonly()}
                dataTestId="customer-information-intacct-id-textInput"
              />
            </div>
          </Show>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 min-w-0">
          <div class="grid grid-cols-1 md:grid-cols-12 gap-4 min-w-0">
            <div class="md:col-span-7 min-w-0">
              <Box class="text-lg font-semibold text-[#123B50]">Stats</Box>
              <Box class={classes.statsBox}>
                <span class="min-w-0 break-words">Credit Limit</span>
                <span class="break-all">
                  {formatAmount(
                    get(customerDetailsStore.customer, 'creditLimit'),
                  )}
                </span>
              </Box>
              <Show when={canSeeNegativeCredit()}>
                <Box class={classes.statsBox}>
                  <span class="min-w-0 break-words">Negative Credit Limit</span>
                  <span class="break-all">
                    {formatAmount(
                      get(
                        customerDetailsStore.customer,
                        'negativeCreditAllowance',
                      ),
                    )}
                  </span>
                </Box>
              </Show>
              <Box class={classes.statsBox}>
                <span class="min-w-0 break-words">Available Credit</span>
                <span class="text-green-800 break-all">
                  {formatAmount(
                    get(
                      customerDetailsStore.customer,
                      'financials.availableCredit',
                    ),
                  )}
                </span>
              </Box>
              <Show when={canSeeNegativeCredit()}>
                <Box class={classes.statsBox}>
                  <Tooltip
                    class="z-1"
                    title={absoluteCreditTooltip()}
                    placement="top"
                    dataTestId="customer-information-absolute-credit-tooltip"
                  >
                    <span class="min-w-0 break-words cursor-pointer">
                      Absolute Credit
                    </span>
                  </Tooltip>
                  <span class="text-green-800 break-all">
                    {formatAmount(handleAbsoluteCredit())}
                  </span>
                </Box>
              </Show>
              <Box class={classes.statsBox}>
                <span class="min-w-0 break-words">Credit Utilization</span>
                <span>{handleCreditUtilization()}</span>
              </Box>
              <Box class={classes.statsBox}>
                <span class="min-w-0 break-words">Claims Balance</span>
                <span class="text-red-600">
                  {formatAmount(
                    get(
                      customerDetailsStore.customer,
                      'financials.openClaimsBalance',
                    ),
                  )}
                </span>
              </Box>
              <Box class={classes.statsBox}>
                <span class="min-w-0 break-words">Last Load Date</span>
                {customerDetailsStore.customer.financials?.lastLoadDate !=
                null ? (
                  <span class="text-gray-500">
                    {DateTime.fromISO(
                      customerDetailsStore.customer.financials.lastLoadDate,
                    ).toFormat('MM/dd/yyyy')}
                  </span>
                ) : (
                  <span class="text-[#777777]">No Loads To Date</span>
                )}
              </Box>
            </div>
            <div class="md:col-span-5 min-w-0">
              <Box class="text-lg font-semibold text-[#123B50]">Reports</Box>
              <Box class={classes.linkBox}>
                <A
                  href={customerUrl.customerOpenInvoices(
                    customerDetailsStore.customer.id as number,
                  )}
                  target="_blank"
                >
                  Open Invoices
                  <OpenInNewIcon class="w-[20px]! shrink-0" />
                </A>
              </Box>
              <Box class={classes.linkBox}>
                <A
                  href={customerUrl.customerPastDueInvoices(
                    customerDetailsStore.customer.id as number,
                  )}
                  target="_blank"
                >
                  Past Due Invoices
                  <OpenInNewIcon class="w-[20px]! shrink-0" />
                </A>
              </Box>
              <Box class={classes.linkBox}>
                <A
                  href={customerUrl.customerOutstandingBalances(
                    customerDetailsStore.customer.id as number,
                  )}
                  target="_blank"
                >
                  All Outstanding Balances
                  <OpenInNewIcon class="w-[20px]! shrink-0" />
                </A>
              </Box>
            </div>
          </div>
          <div class="min-w-0 flex flex-col gap-2">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 min-w-0">
              <TextField
                label="PC Miler Version"
                type="number"
                maxLength={10}
                placeholder="Default to latest version"
                value={customerDetailsStore.customer.pcMilerVersion ?? ''}
                onChange={(value: string) => handlePcMilerVersionChange(value)}
                dataTestId="customer-information-pc-miler-version-textInput"
              />
              <div class="flex items-end min-w-0">
                <Checkbox
                  size="xs"
                  color="info"
                  class="ml-[-9px]"
                  label={
                    <Box class="flex flex-wrap items-center gap-1">
                      Require Tracking
                      <Tooltip
                        class="z-1 shrink-0"
                        title="Tracking requirement notification will be displayed in loads."
                        placement="left"
                        dataTestId="customer-information-require-tracking-tooltip"
                      >
                        <InformationIcon class="text-[24px] text-[#026EA1] cursor-pointer shrink-0" />
                      </Tooltip>
                    </Box>
                  }
                  name="alwaysTrackLoads"
                  checked={customerDetailsStore.customer.alwaysTrackLoads}
                  onChange={(value) =>
                    updateCustomerDetailsState({
                      alwaysTrackLoads: value,
                    })
                  }
                  dataTestId="customer-information-always-track-loads-checkboxInput"
                />
              </div>
            </div>
            <Box class="flex flex-wrap font-semibold text-[14px] items-center gap-1">
              Tracking Visibility Provider
              <Tooltip
                class="z-1 shrink-0"
                placement="left"
                title="Used to send tracking data from the load tracking provider to the customer's preferred tracking visibility provider selected here. A valid Tracking ID must be entered and configured with the load's tracking provider"
                dataTestId="customer-information-tracking-visibility-provider-tooltip"
              >
                <InformationIcon class="text-[24px] text-[#026EA1] cursor-pointer shrink-0" />
              </Tooltip>
            </Box>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 min-w-0">
              <SelectInput
                label="Provider"
                value={
                  customerDetailsStore.customer.trackingVisibilityProvider ??
                  'null'
                }
                onChange={(val) => {
                  updateCustomerDetailsState({
                    trackingVisibilityProvider: val === 'null' ? null : val,
                  });
                }}
                menuItems={trackingVisibilityProviderList.map((item) => ({
                  label: item.label,
                  value: String(item.value),
                }))}
                dataTestId={formatDataTestId(
                  'Tracking visibility provider',
                  'selectInput',
                )}
              />
              <TextField
                label="Tracking ID"
                placeholder="Add tracking ID"
                value={customerDetailsStore.customer.trackingXId ?? ''}
                onChange={(value: unknown) => {
                  updateCustomerDetailsState({
                    trackingXId: value as number,
                  });
                }}
                class="h-full"
                maxLength={100}
                dataTestId="customer-information-tracking-id-textInput"
              />
            </div>
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 min-w-0">
          <div class="min-w-0">
            <Box class="font-semibold text-[14px] mb-2">
              Auto Email Settings
            </Box>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Checkbox
                size="xs"
                color="info"
                class="ml-[-9px]"
                label="Do Not Email OBR"
                name="optOutBalanceEmail"
                checked={customerDetailsStore.customer.optOutBalanceEmail}
                onChange={(value) =>
                  updateCustomerDetailsState({ optOutBalanceEmail: value })
                }
                disabled={isReadonly()}
                dataTestId="customer-information-opt-out-balance-email-checkboxInput"
              />
              <Checkbox
                size="xs"
                color="info"
                class="ml-[-9px]"
                label="Do Not Email Past Due"
                name="optOutPastDueDateBalanceEmail"
                checked={
                  customerDetailsStore.customer.optOutPastDueDateBalanceEmail
                }
                onChange={(value) =>
                  updateCustomerDetailsState({
                    optOutPastDueDateBalanceEmail: value,
                  })
                }
                disabled={isReadonly()}
                dataTestId="customer-information-opt-out-past-due-date-balance-email-checkboxInput"
              />
            </div>
          </div>
          <div class="min-w-0">
            <Box class="font-semibold text-[14px] mb-2">Fuel Recovery</Box>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 items-end">
              <div class="flex items-center gap-2 min-w-0">
                <Checkbox
                  size="xs"
                  color="info"
                  class="ml-[-9px] shrink-0"
                  name="FuelRecovery"
                  checked={
                    customerDetailsStore.customer.fuelReimbursement
                      .fuelReimbursementEnabled
                  }
                  onChange={(value) =>
                    updateCustomerDetailsState({
                      fuelReimbursement: {
                        integrationKey: '',
                        fuelReimbursementEnabled: value,
                      },
                    })
                  }
                  dataTestId="customer-information-fuel-recovery-checkboxInput"
                />
                <div class="flex-1 min-w-0">
                  <SelectInput
                    label=""
                    disabled={
                      !customerDetailsStore.customer.fuelReimbursement
                        .fuelReimbursementEnabled
                    }
                    value="Breakthrough Fuel"
                    menuItems={[
                      {
                        label: 'Breakthrough Fuel',
                        value: 'Breakthrough Fuel',
                      },
                    ]}
                    onChange={() => undefined}
                    dataTestId={formatDataTestId(
                      'Fuel recovery provider',
                      'selectInput',
                    )}
                  />
                </div>
              </div>
              <TextField
                label="Integration Key"
                placeholder="Add Integration Key"
                disabled={
                  !customerDetailsStore.customer.fuelReimbursement
                    .fuelReimbursementEnabled
                }
                value={
                  customerDetailsStore.customer.fuelReimbursement
                    .integrationKey ?? ''
                }
                onChange={(value: unknown) => {
                  updateCustomerDetailsState({
                    fuelReimbursement: {
                      ...customerDetailsStore.customer.fuelReimbursement,
                      integrationKey: value as string,
                    },
                  });
                }}
                class="h-full"
                maxLength={100}
                dataTestId="customer-information-integration-key-textInput"
                error={
                  customerDetailsStore.customer.fuelReimbursement
                    .fuelReimbursementEnabled &&
                  !customerDetailsStore.customer.fuelReimbursement
                    .integrationKey
                    ? 'Integration Key is required'
                    : ''
                }
              />
            </div>
          </div>
        </div>
        <div class="flex flex-col gap-3 mt-3 min-w-0">
          <TextField
            multiline
            label="Rate Confirmation Special Instructions"
            value={customerDetailsStore.customer.loadInstructions ?? ''}
            rows={5}
            placeholder={loadInstructionsPlaceholder}
            onChange={(data) =>
              updateCustomerDetailsState({
                loadInstructions: data,
              })
            }
            maxLength={5000}
            dataTestId="customer-information-rate-confirmation-special-instructions-textAreaField"
          />
          <TextField
            multiline
            label="BOL Special Instructions"
            value={customerDetailsStore.customer.bolInstructions ?? ''}
            rows={5}
            placeholder={bolInstructionsPlaceholder}
            onChange={(data) =>
              updateCustomerDetailsState({
                bolInstructions: data,
              })
            }
            maxLength={2000}
            dataTestId="customer-information-bol-special-instructions-textAreaField"
          />
          <TextField
            multiline
            label="Customer Point of Sale Notes"
            value={customerDetailsStore.customer.pointOfSaleInstructions ?? ''}
            rows={5}
            placeholder={pointOfSaleInstructionsPlaceholder}
            onChange={(data) =>
              updateCustomerDetailsState({
                pointOfSaleInstructions: data,
              })
            }
            maxLength={2000}
            dataTestId="customer-information-point-of-sale-notes-textAreaField"
          />
        </div>
      </>
    </CollapsibleCard>
  );
};

export default CustomerInformation;
